import * as React from 'react'

import { Button, Spinner } from '@factorialco/deprecated-design-system'
import {
  AccessForSelectFragment,
  PerformanceProcessPersonalResultsEvaluationsDocument,
  PerformanceReviewEvaluationSiblingReviewersDocument,
  PerformanceReviewEvaluationsDocument,
  PerformanceReviewOverviewPendingEvaluationsTaskDocument,
  type PerformanceReviewerStrategiesStrategyEnum,
  ReplaceReviewerPerformanceReviewEvaluationDocument,
} from 'generated/resources/graphql'

import { AccessSelector } from 'modules/core/components/AccessSelector'
import Modal from 'modules/core/components/Modal'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface Props {
  onClose: () => void
  evaluationId: number
  evaluationType: PerformanceReviewerStrategiesStrategyEnum
  excludedAccessIds?: Set<number>
}

export const ReplaceReviewerModal = ({
  onClose,
  evaluationId,
  evaluationType,
  excludedAccessIds: providedExcludedAccessIds,
}: Props) => {
  const [selectedAccess, setSelectedAccess] = React.useState<AccessForSelectFragment | null>(null)

  const { data, loading } = useQuery(PerformanceReviewEvaluationSiblingReviewersDocument, {
    variables: { evaluationId, evaluationType },
    skip: providedExcludedAccessIds !== undefined,
  })

  const excludedAccessIds = React.useMemo(() => {
    if (providedExcludedAccessIds) {
      return providedExcludedAccessIds
    }

    const evaluations = data?.performance?.reviewEvaluation?.reviewProcessTarget.evaluations ?? []
    const reviewerIds = evaluations
      .map((e) => e.reviewerAccess?.id)
      .filter((id): id is number => id !== undefined)
    return reviewerIds
  }, [data, providedExcludedAccessIds])

  const [replaceReviewerEmployee, { loading: replacing }] = useMutation(
    ReplaceReviewerPerformanceReviewEvaluationDocument,
    {
      extractorFn: (result) =>
        result.performanceMutations.replaceReviewerPerformanceReviewEvaluation,
      refetchQueries: [
        PerformanceReviewEvaluationsDocument,
        PerformanceReviewOverviewPendingEvaluationsTaskDocument,
        PerformanceProcessPersonalResultsEvaluationsDocument,
      ],
    }
  )

  const handleReviewerChange = async () => {
    if (!selectedAccess) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.error'),
      })
      return
    }

    const response = await replaceReviewerEmployee({
      variables: {
        newReviewerAccessId: String(selectedAccess.id),
        evaluationId: String(evaluationId),
      },
    })

    const result = handleResult(response, (errors) => {
      if (errors.messages) {
        app.setFlash({
          type: 'wrong',
          message: errors.messages,
        })
      }
    })

    if (result.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    }
    onClose()
  }

  return (
    <Modal
      illustration="user-male-female"
      title={i18n.t('performance_process.reviews.evaluations.replace_reviewer')}
      description={i18n.t('performance_process.reviews.evaluations.replace_reviewer_description')}
      onClose={onClose}
      onSubmit={handleReviewerChange}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={!selectedAccess || replacing || loading} submit>
            {i18n.t('common.save')}
          </Button>
        </>
      }
      disableFocusTrap
    >
      {loading ? (
        <Spinner />
      ) : (
        <AccessSelector
          mandatory
          withAvatar
          width="s400"
          value={selectedAccess}
          multiple={false}
          onSelect={(value: AccessForSelectFragment | null) => {
            setSelectedAccess(value)
          }}
          emptyLabel={i18n.t(
            'performance_process.reviews.basic_information_section.owners.empty_results'
          )}
          excludeIds={Array.from(excludedAccessIds)}
          withRegularAccess
          terminated={false}
        />
      )}
    </Modal>
  )
}
